import { TransferKeys } from "@/modules/transaction/common/enum";
import type { IGroupTransferBeneficiary } from "@/modules/transaction/common/types";
import { getBeneficiaryLegalStatusOptions } from "@/modules/transaction/helpers";
import { CURRENCY } from "@/shared/common/constants";
import { BeneficiaryType } from "@/shared/common/enums";
import type { TSetError } from "@/shared/common/types";
import { Select, TransferBeneficiaries } from "@/shared/components";
import { isAmeriaAccount, isValidAccount, isValidAmount } from "@/shared/helpers";
import { useAppSelector, useGetAccountHolder } from "@/shared/hooks";
import { debounce } from "@/shared/utils";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isEdit?: boolean;
  beneficiary: IGroupTransferBeneficiary | null;
  setIsAccountNumberInvalid: Dispatch<SetStateAction<boolean>>;
}

export const AddEditBeneficiaryForm = ({
  isEdit,
  beneficiary,
  setIsAccountNumberInvalid,
}: Props) => {
  const { t } = useTranslation();
  const { setValue, setError, watch, clearErrors } = useFormProps();
  const { getAccountInfo, accountData } = useGetAccountHolder({ setIsAccountNumberInvalid });
  const [beneficiaryName, setBeneficiaryName] = useState<string>("");
  const currency = useAppSelector((state) => state.transactions.groupTransferCurrency);

  const handleChangeBeneficiaryAccount = useMemo(
    () => debounce((account: string) => {
      if (account !== null) {
        setValue(TransferKeys.Beneficiary, "");
        setValue(TransferKeys.BeneficiaryLegalStatus, "");

        getAccountInfo({
          account,
          setError: setError as TSetError,
          clearErrors,
          setValue,
          getBeneficiaryName: () => beneficiaryName,
          beneficiaryNameKey: TransferKeys.Beneficiary,
          creditAccountKey: TransferKeys.Account,
        });
      }
    }, 700),
    [],
  );

  const values = watch?.() || {};

  useEffect(() => {
    setValue(TransferKeys.BankName, accountData?.bankName);
  }, [accountData]);

  useEffect(() => {
    if (isEdit) {
      setValue(TransferKeys.Account, beneficiary?.receiverAccount);
      setValue(TransferKeys.Beneficiary, beneficiary?.receiverName);
      setValue(TransferKeys.Amount, beneficiary?.amount);
      setValue(TransferKeys.BeneficiaryLegalStatus, beneficiary?.beneficiaryLegalStatus);
    }
  }, [isEdit, beneficiary]);

  return (
    <>
      <TransferBeneficiaries
        name="account"
        className="mb-8"
        setBeneficiaryName={setBeneficiaryName}
        entryType={BeneficiaryType.Account}
        label={t("transfer.receiverAccount")}
        onChange={(v) => handleChangeBeneficiaryAccount(v)}
        placeholder={t("transfer.typeOrChooseAnAccount")}
      />
      <Text className="mt-8" type="tertiary">
        {accountData?.bankName}
      </Text>
      {isValidAccount(values.account) &&
        !isAmeriaAccount(values.account) && (
          <FormField
            name="beneficiaryLegalStatus"
            className="mb-24 mt-24"
            dataId="beneficiaryLegalStatus"
            shouldValidate
            errorMessageIcon="infoFilled"
            As={(props) => (
              <Select
                {...props}
                required
                options={getBeneficiaryLegalStatusOptions({ t })}
                label={t("transfer.beneficiaryLegalStatus")}
                placeholder={t("transfer.chooseBeneficiaryLegalStatus")}
              />
            )}
          />
        )}
      {accountData?.holder && isAmeriaAccount(values.account) ? (
        <div className="mt-24 mb-24">
          <div className="flexbox justify-content--between mb-16">
            <Text className="mr-16">{t("transfer.accountHolder")}</Text>
            <Text className="text-align--end">{accountData?.holder}</Text>
          </div>
          <Divider
            color="light"
            type="primary"
            isHorizontal
          />
        </div>
      ) : (
        <FormField
          name="beneficiary"
          className="mb-24 mt-24"
          errorMessageIcon="infoFilled"
          As={(props) => (
            <Input
              {...props}
              required
              isTrimValues
              label={t("transfer.beneficiaryName")}
              placeholder={t("transfer.enterBeneficiaryName")}
            />
          )}
        />
      )}
      <FormField
        name="amount"
        errorMessageIcon="infoFilled"
        As={(props) => (
          <Input
            {...props}
            required
            type="numeric"
            allowNegative={false}
            allowLeadingZeros={false}
            label={t("general.amount")}
            placeholder={t("general.enterAmount")}
            thousandSeparator=","
            isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
            decimalScale={currency === CURRENCY.AMD.label ? 1 : 2}
            rightIconProps={{
              Component: currency ? CURRENCY[currency]?.symbolIcon : "",
            }}
          />
        )}
      />
    </>
  );
};
